const NON_COLOR_TEXT_SUFFIXES = new Set([
  'xs',
  'sm',
  'base',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
  'left',
  'center',
  'right',
  'justify',
  'start',
  'end',
  'wrap',
  'nowrap',
  'balance',
  'pretty',
  'ellipsis',
  'clip',
]);

const NON_COLOR_TEXT_SUFFIX_PREFIXES = [
  'opacity-',
  'shadow-',
  'decoration-',
  'underline-',
  'indent-',
];

const NON_COLOR_BG_SUFFIXES = new Set([
  'auto',
  'cover',
  'contain',
  'fixed',
  'local',
  'scroll',
  'top',
  'bottom',
  'left',
  'right',
  'center',
  'left-top',
  'left-bottom',
  'right-top',
  'right-bottom',
  'repeat',
  'no-repeat',
  'repeat-x',
  'repeat-y',
  'repeat-round',
  'repeat-space',
  'none',
]);

const NON_COLOR_BG_SUFFIX_PREFIXES = [
  'clip-',
  'origin-',
  'blend-',
  'gradient-',
  'linear-',
  'radial',
  'conic',
  'opacity-',
  'position-',
  'size-',
];

const ARBITRARY_NON_COLOR_TYPE =
  /^\[(length|url|image|angle|size|position|percentage|number|integer)[:(]/;
const ARBITRARY_DIMENSION =
  /^\[-?[\d.]+(px|r?em|%|vh|vw|vmin|vmax|ch|ex|cm|mm|in|pt|pc|fr)\]$/;

const stripVariants = (token: string): string => {
  let depth = 0;
  let lastTopLevelColon = -1;
  for (let i = 0; i < token.length; i++) {
    const char = token[i];
    if (char === '[') {
      depth += 1;
    } else if (char === ']') {
      depth = Math.max(0, depth - 1);
    } else if (char === ':' && depth === 0) {
      lastTopLevelColon = i;
    }
  }
  return lastTopLevelColon === -1 ? token : token.slice(lastTopLevelColon + 1);
};

const normalizeClass = (token: string): string =>
  stripVariants(token).replace(/^!/, '').replace(/!$/, '');

const isArbitraryValue = (suffix: string): boolean =>
  suffix.startsWith('[') && suffix.endsWith(']');

const isArbitraryNonColor = (suffix: string): boolean =>
  ARBITRARY_NON_COLOR_TYPE.test(suffix) || ARBITRARY_DIMENSION.test(suffix);

const hasColorUtility = (
  className: string | undefined,
  prefix: 'bg-' | 'text-',
  nonColorSuffixes: Set<string>,
  nonColorSuffixPrefixes: string[],
): boolean => {
  if (!className) {
    return false;
  }

  return className
    .trim()
    .split(/\s+/)
    .some((token) => {
      const normalized = normalizeClass(token);
      if (!normalized.startsWith(prefix)) {
        return false;
      }

      const suffix = normalized.slice(prefix.length);
      if (suffix.length === 0) {
        return false;
      }
      if (nonColorSuffixes.has(suffix)) {
        return false;
      }
      if (nonColorSuffixPrefixes.some((p) => suffix.startsWith(p))) {
        return false;
      }
      if (isArbitraryValue(suffix)) {
        return !isArbitraryNonColor(suffix);
      }

      return true;
    });
};

export const hasTailwindBackgroundColor = (className?: string): boolean =>
  hasColorUtility(
    className,
    'bg-',
    NON_COLOR_BG_SUFFIXES,
    NON_COLOR_BG_SUFFIX_PREFIXES,
  );

export const hasTailwindTextColor = (className?: string): boolean =>
  hasColorUtility(
    className,
    'text-',
    NON_COLOR_TEXT_SUFFIXES,
    NON_COLOR_TEXT_SUFFIX_PREFIXES,
  );
